import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { PCHIndexedDBService } from '../pch-indexed-db/pch-indexed-db.service';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { catchError, EMPTY, from, map, Observable, switchMap, tap } from 'rxjs';
import { EventService, NotificationIdentifier } from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { PCHAlertServiceMessageType } from './pch-alert.enum';
import { PCHAlert } from './pch-alert.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class PCHAlertService {
    private destroyRef = inject(DestroyRef);

    private sw: ServiceWorker | null = null;

    public constructor(
        private readonly pchIndexedDBService: PCHIndexedDBService,
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        private readonly eventService: EventService,
        private readonly translate: TranslateService,
    ) {
        this.initServiceWorker();
    }

    public cleanUpAlertPostCRM(notification: NotificationIdentifier): void {
        const id = this.alertIdFromNotification(notification);
        this.pchIndexedDBService.deleteAlerts(id).subscribe();
    }

    public initServiceWorker(): void {
        from(navigator.serviceWorker.ready)
            .pipe(
                tap((reg: ServiceWorkerRegistration) => {
                    this.sw = reg.active;
                    this.listenToServiceWorker();
                }),
                switchMap(() => this.scheduleNextReminder()),
                catchError((err) => {
                    this.log.error('Failed to initialize reminder system:', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }
    // Adds a new alert and immediately re-schedules the next due reminder.

    public addReminder(dateTime: Date, notification: NotificationIdentifier): Observable<PCHAlert | null | undefined> {
        const message = this.translate.instant('NOTIFICATION_DETAILS_PCH_ALERT_TITLE');
        const alert: PCHAlert = {
            time: dateTime.getTime(),
            title: `${notification.qmNum} (${notification.maNum})`,
            message,
            id: this.alertIdFromNotification(notification),
        };
        return this.pchIndexedDBService.savePCHAlertToIndexedDB(alert).pipe(
            switchMap(() => this.scheduleNextReminder()),
            catchError((err) => {
                this.log.error('Error in addReminder:', err);
                return EMPTY;
            }),
        );
    }
    // Schedules the next upcoming reminder by sending it to the service worker.

    private scheduleNextReminder(): Observable<PCHAlert | null> {
        return this.pchIndexedDBService.loadAllPCHAlertsFromIndexedDB().pipe(
            map((alerts) => this.findNextReminder(alerts)),
            tap((next) => {
                if (next) {
                    this.sendReminderToSW(next);
                }
            }),
            catchError((err) => {
                this.log.error('Error in scheduleNextReminder:', err);
                return EMPTY;
            }),
        );
    }

    // Sends the next alert to the custom service worker to schedule its timer.

    private sendReminderToSW(alerts: PCHAlert): void {
        if (!this.sw) {
            return;
        }

        this.sw.postMessage({
            type: PCHAlertServiceMessageType.SetNextReminder,
            payload: {
                time: alerts.time,
                title: alerts.title,
                message: alerts.message,
                id: alerts.id,
            },
        });
    }

    // Listens for messages from the service worker.

    private listenToServiceWorker(): void {
        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
            const type = event.data?.type;

            if (type === PCHAlertServiceMessageType.ReminderFired) {
                const firedId = event.data?.payload?.id;
                this.eventService.triggerOpenPCHAlertModal();
                this.handleReminderFired(firedId).subscribe();
            }
        });
    }

    private handleReminderFired(id: string): Observable<PCHAlert | null | undefined> {
        return this.pchIndexedDBService.deleteAlerts(id).pipe(
            switchMap(() => this.scheduleNextReminder()),
            catchError((err) => {
                this.log.error('Error in handleReminderFired:', err);
                return EMPTY;
            }),
        );
    }

    // Returns the next alert (closest upcoming time).

    private findNextReminder(alerts: PCHAlert[]): PCHAlert | null {
        const now: number = Date.now();

        const upcomingAlert: PCHAlert[] = alerts.filter((r) => r.time >= now).sort((a, b) => a.time - b.time);

        return upcomingAlert[0] ?? null;
    }

    private alertIdFromNotification(notification: NotificationIdentifier): string {
        return `${notification.qmNum}-${notification.maNum}`;
    }
}
